<template>
<div>
  <RecordPlayer
    v-if="playerOptions"
    :options="playerOptions"
    :size-styles="{width: '100%', height: '400px'}"
    ref="playerComponent"
    @timeupdate="playerTimeUpdateHandler"
    @pause="playing = false"
    @ended="playing = false"
    @playing="playing = true"
  />
  <div ref="slider" class="cut-slider">
    <div class="frames">
      <div
        v-for="(frame, index) in frames" :key="index"
        :style="[frameStyle, frame]"
      ></div>
    </div>
    <div class="mask left" :style="leftMaskStyle"></div>
    <div
      class="controller left"
      @mousedown="dragLeftControlStart"
      @touchstart="dragTouchLeftControlStart"
      ref="leftControl"
      :style="leftControlStyle"
    ></div>
    <div
      class="active-section"
      @click="setRunnerPos"
    >
      <div v-if="showControlTime" class="left">
        {{ leftControlVideoPositionFormatted }}
      </div>
      <div class="video-duration">{{ videoSectionDurationFormatted }}</div>
      <div v-if="showControlTime" class="right">
        {{ rightControlVideoPositionFormatted }}
      </div>
    </div>
    <div
      class="controller right"
      @mousedown="dragRightControlStart"
      @touchstart="dragTouchRightControlStart"
      ref="rightControl"
      :style="rightControlStyle"
    ></div>
    <div class="mask right" :style="rightMaskStyle"></div>
    <div class="runner" :style="runnerStyle"></div>
  </div>
  <div class="text-center">
    <v-btn class="btn-primary play-btn" flat fab @click="playRange">
      <v-icon v-if="playing" dark>pause</v-icon>
      <v-icon v-else dark>play_arrow</v-icon>
    </v-btn>
  </div>
</div>
</template>

<script>
import debounce from 'lodash.debounce'
import { getRecordPlayerOptions } from '@/utils'
import RecordPlayer from './RecordPlayer.vue'

export default {
  props: {
    record: Object,
  },

  components: {
    RecordPlayer,
  },

  data () {
    return {
      sprite: null,
      leftControlPos: 0,
      rightControlPos: 0,
      runnerPos: 0,
      sliderWidth: 0,
      playerOptions: null,
      frames: [],
      playing: false,
    }
  },

  mounted () {
    this.sprite = this.record.media.find(el => el.name === 'thumbspritehoriz')
    this.setSliderWidth()
    this.playerOptions = this.getRecordPlayerOptions(this.record)
    window.addEventListener('resize', this.onWindowResize)
    this.calcFrames()
  },

  computed: {
    frameStyle () {
      if (this.sprite) return { 'background-image': `url(${this.sprite.url})` }
      return {}
    },
    leftControlStyle () {
      return { left: this.leftControlPos + 'px' }
    },
    rightControlStyle () {
      return { right: this.rightControlPos + 'px' }
    },
    leftMaskStyle () {
      return { width: this.leftControlPos + 'px' }
    },
    rightMaskStyle () {
      return { width: this.rightControlPos + 'px' }
    },
    runnerStyle () {
      return {
        left: this.runnerPos + 'px',
      }
    },
    pixelWeight () {
      return this.$props.record.duration / this.sliderWidth
    },
    leftControlVideoPosition () {
      return this.leftControlPos * this.pixelWeight
    },
    leftControlVideoPositionFormatted () {
      return this.formatDuration(this.leftControlVideoPosition)
    },
    rightControlVideoPosition () {
      return (this.sliderWidth - this.rightControlPos) * this.pixelWeight
    },
    rightControlVideoPositionFormatted () {
      return this.formatDuration(this.rightControlVideoPosition)
    },
    videoSectionDuration () {
      return this.rightControlVideoPosition - this.leftControlVideoPosition
    },
    videoSectionDurationFormatted () {
      return this.formatDuration(this.videoSectionDuration)
    },
    showControlTime () {
      return this.sliderWidth - this.leftControlPos - this.rightControlPos > 150
    }
  },

  methods: {
    getRecordPlayerOptions,
    dragLeftControlStart (e) {
      e.preventDefault()
      document.addEventListener('mousemove', this.dragLeftControl)
      document.addEventListener('mouseup', this.dragControlStop)
    },
    dragTouchLeftControlStart (e) {
      e.preventDefault()
      document.addEventListener('touchmove', this.dragTouchLeftControl)
      document.addEventListener('touchend', this.dragTouchControlStop)
    },
    dragRightControlStart (e) {
      e.preventDefault()
      document.addEventListener('mousemove', this.dragRightControl)
      document.addEventListener('mouseup', this.dragControlStop)
    },
    dragTouchRightControlStart (e) {
      e.preventDefault()
      document.addEventListener('touchmove', this.dragTouchRightControl)
      document.addEventListener('touchend', this.dragTouchControlStop)
    },
    dragLeftControl (e) {
      e.preventDefault()
      this.dragControl('left', e.clientX - this.$refs.slider.offsetLeft)
    },
    dragTouchLeftControl (e) {
      this.dragControl(
        'left', e.touches[0].clientX - this.$refs.slider.offsetLeft
      )
    },
    dragRightControl (e) {
      e.preventDefault()
      this.dragControl(
        'right', this.$refs.slider.offsetLeft + this.sliderWidth - e.clientX
      )
    },
    dragTouchRightControl (e) {
      this.dragControl(
        'right', this.$refs.slider.offsetLeft + this.sliderWidth - e.touches[0].clientX
      )
    },
    dragControl (side, pos) {
      // part common for left and right control
      const oppositePos = this[(side === 'left' ? 'right' : 'left') + 'ControlPos']
      if (pos < 0) pos = 0
      if (this.sliderWidth - pos - oppositePos > 10) {
        this[side + 'ControlPos'] = pos
        if (side === 'right') pos = this.sliderWidth - pos
        this.setPlayerPosition(pos)
      }
    },
    dragControlStop (e) {
      e.preventDefault()
      document.removeEventListener('mousemove', this.dragLeftControl)
      document.removeEventListener('mousemove', this.dragRightControl)
      document.removeEventListener('mouseup', this.dragControlStop)
      this.$emit('range-update', {
        start: Math.round(this.leftControlVideoPosition * 100) / 100,
        stop: Math.round(this.rightControlVideoPosition * 100) / 100,
      })
    },
    dragTouchControlStop (e) {
      e.preventDefault()
      document.removeEventListener('touchmove', this.dragTouchLeftControl)
      document.removeEventListener('touchmove', this.dragTouchRightControl)
      document.removeEventListener('touchend', this.dragTouchControlStop)
      this.$emit('range-update', {
        start: Math.round(this.leftControlVideoPosition * 100) / 100,
        stop: Math.round(this.rightControlVideoPosition * 100) / 100,
      })
    },
    formatDuration (seconds) {
      let m = parseInt(seconds / 60)
      let s = parseInt(seconds - m * 60)
      let ds = '0'
      if (String(seconds).indexOf('.') !== -1) {
        ds = String(seconds).split('.')[1][0]
      }
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${ds}`
    },
    setRunnerPos (e) {
      // set runner position by click on active area
      this.runnerPos = e.clientX - this.$refs.slider.offsetLeft
      this.setPlayerPosition(this.runnerPos)
    },
    playerTimeUpdateHandler (event) {
      if (!this.playing) return
      const time = event.target.currentTime
      // position event has delay 50 - 200ms
      // so we need to set apuse a little bit earlier
      if (time + 0.15 >= this.rightControlVideoPosition) {
        this.setPlayerPosition(this.leftControlPos)
      }
      this.runnerPos = time / this.pixelWeight
    },
    play () {
      this.$refs.playerComponent.player.play()
    },
    pause () {
      this.$refs.playerComponent.player.pause()
    },
    setPlayerPosition (pos) {
      this.$refs.playerComponent.player.currentTime(this.pixelWeight * pos)
    },
    currentTime () {
      return this.$refs.playerComponent.player.currentTime()
    },
    playRange () {
      if (this.playing) {
        this.pause()
      } else {
        if (
          this.currentTime() < this.leftControlVideoPosition ||
          this.currentTime() + 0.2 >= this.rightControlVideoPosition
        ) {
          this.setPlayerPosition(this.leftControlPos)
        }
        this.play()
      }
    },
    setSliderWidth () {
      this.sliderWidth = this.$refs.slider.offsetWidth
    },
    onWindowResize: debounce(() => {
      this.setSliderWidth()
    }, 100),
    calcFrames () {
      const sprite = this.sprite
      const framesInSprite = Math.floor(sprite.width / sprite.sprite_interval_h)
      // 60 - is height of slider
      const frameWidth = 60 * sprite.width / framesInSprite / sprite.height
      const style = {}
      let framesNum = Math.ceil(this.sliderWidth / frameWidth)
      if (framesNum > framesInSprite) {
        framesNum = framesInSprite
        style['width'] = frameWidth + 'px'
      } else {
        style['flex-grow'] = 1
      }
      const step = Math.round(framesInSprite / framesNum) || 1
      this.framesNum = framesNum
      for (let i = 0; i < framesNum; i++) {
        let bgPos = Math.round(-step * i * frameWidth)
        if (step * i >= framesInSprite) bgPos += frameWidth
        this.frames.push(
          Object.assign({ 'background-position-x': bgPos + 'px' }, style)
        )
      }
    }
  }, // methods

  beforeDestroy () {
    document.removeEventListener('mouseup', this.dragControlStop)
    window.removeEventListener('resize', this.onWindowResize)
  },
}
</script>

<style lang="sass" scoped>
.cut-slider
  height: 60px
  border: 1px solid black
  position: relative
  display: flex
  margin-bottom: 40px
  margin-top: 15px
  background-color: rgba(3.9%, 3.9%, 3.9%, 0.522)
  box-sizing: content-box
.active-section
  height: 100%
  flex-grow: 1
  position: relative
  & > div
    position: absolute
    bottom: -24px
  .video-duration
    left: 50%
    transform: translateX(-50%)
  .left
    left: 0
    transform: translateX(-20%)
  .right
    right: 0
    transform: translateX(20%)
.controller
  height: 100%
  width: 10px
  background: red
  cursor: ew-resize
  position: absolute
  border-radius: 20%
  z-index: 2
  &.left
    transform: translateX(-50%)
  &.right
    transform: translateX(50%)
.mask
  background-color: #c9c9d2bf
  height: 100%
  z-index: 1
.runner
  width: 1px
  background-color: #fff
  position: absolute
  height: 100%
.frames
  display: flex
  position: absolute
  width: 100%
  height: 100%
  justify-content: space-between
  & > div
    height: 100%
    background-size: cover
.play-btn
  .v-icon
    height: auto
</style>



// WEBPACK FOOTER //
// RecordCutSlider.vue?69eb7be7